import {useCallback, useMemo, useState} from "react";

export const countDashboardChanges = (
  saved: ReadonlyArray<string>,
  draft: ReadonlyArray<string>,
): number => {
  const savedSet = new Set(saved);
  const draftSet = new Set(draft);
  let count = 0;

  draftSet.forEach(id => {
    if (!savedSet.has(id)) {
      count += 1;
    }
  });

  savedSet.forEach(id => {
    if (!draftSet.has(id)) {
      count += 1;
    }
  });

  return count;
};

type PersistDashboardChange = (params: {
  userId: string;
  dashboard: {dashboardId: string; checked: boolean};
}) => Promise<unknown>;

type Params = Readonly<{
  userId: string;
  savedCheckedIds: ReadonlyArray<string>;
  persistDashboardChange: PersistDashboardChange;
  onPersistSuccess: () => void;
}>;

export const useQuicksightDashboardDraft = ({
  userId,
  savedCheckedIds,
  persistDashboardChange,
  onPersistSuccess,
}: Params) => {
  const [draftCheckedIds, setDraftCheckedIds] = useState<
    ReadonlyArray<string> | null
  >(null);

  const checkedValues = draftCheckedIds ?? savedCheckedIds;

  const pendingChangeCount = useMemo(
    () =>
      draftCheckedIds
        ? countDashboardChanges(savedCheckedIds, draftCheckedIds)
        : 0,
    [draftCheckedIds, savedCheckedIds],
  );

  const setDashboardChecked = useCallback(
    (dashboardId: string, checked: boolean) => {
      setDraftCheckedIds(current => {
        const base = current ?? savedCheckedIds;

        if (checked) {
          return base.includes(dashboardId) ? base : [...base, dashboardId];
        }

        return base.filter(id => id !== dashboardId);
      });
    },
    [savedCheckedIds],
  );

  const setGroupChecked = useCallback(
    (dashboardIds: ReadonlyArray<string>, checked: boolean) => {
      setDraftCheckedIds(current => {
        const base = current ?? savedCheckedIds;

        if (checked) {
          const next = new Set(base);

          dashboardIds.forEach(id => next.add(id));

          return [...next];
        }

        const idsToRemove = new Set(dashboardIds);

        return base.filter(id => !idsToRemove.has(id));
      });
    },
    [savedCheckedIds],
  );

  const discard = useCallback(() => {
    setDraftCheckedIds(null);
  }, []);

  const save = useCallback(async () => {
    if (!draftCheckedIds || pendingChangeCount === 0) {
      return;
    }

    const savedSet = new Set(savedCheckedIds);
    const draftSet = new Set(draftCheckedIds);

    const changes = [
      ...draftCheckedIds
        .filter(id => !savedSet.has(id))
        .map(
          dashboardId =>
            [dashboardId, true] as const,
        ),
      ...savedCheckedIds
        .filter(id => !draftSet.has(id))
        .map(
          dashboardId =>
            [dashboardId, false] as const,
        ),
    ];

    await Promise.all(
      changes.map(([dashboardId, checked]) =>
        persistDashboardChange({
          userId,
          dashboard: {dashboardId, checked},
        }),
      ),
    );

    setDraftCheckedIds(null);
    onPersistSuccess();
  }, [
    draftCheckedIds,
    onPersistSuccess,
    pendingChangeCount,
    persistDashboardChange,
    savedCheckedIds,
    userId,
  ]);

  return {
    checkedValues,
    discard,
    pendingChangeCount,
    save,
    setDashboardChecked,
    setGroupChecked,
  };
};

export type QuicksightDashboardDeferredSaveHandlers = Readonly<{
  discard: () => void;
  pendingChangeCount: number;
  save: () => Promise<void>;
}>;
